import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

import { defaultFiltersByFolders } from "../../../Utils/utils"
import { preferencesSuccess } from "../../User/redux/user.actions"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getRequestsAutoRefundFilterOptionsRequest: null,
  getRequestsAutoRefundFilterOptionsSuccess: ["payload"], // payload: { data }
  getRequestsAutoRefundFilterOptionsFailure: ["payload"], // payload: { data }

  getRequestsAutoRefundRequest: ["payload"], // payload: { params }
  getRequestsAutoRefundSuccess: ["payload"], // payload: { data }

  updateRequestsAutoRefundRequest: ["payload"], // payload: { params }
  updateRequestsAutoRefundSuccess: ["payload"], // payload: { data }

  deleteRequestsAutoRefundRequest: ["payload"], // payload: { params }
  deleteRequestsAutoRefundSuccess: ["payload"], // payload: { data }

  requestsAutoRefundFailure: ["payload"], // payload: { errors }

  clearRequestsAutoRefundFilters: ["payload"], // payload: { data }
  requestsAutoRefundSetProp: ["key", "value"],

  getRequestsAutoRefundExportRequest: ["payload"], // payload: { business_id }
  getRequestsAutoRefundExportSuccess: ["payload"], // payload: { url }
  getRequestsAutoRefundExportFailure: ["payload"], // payload: { errors }

  logout: null
})

export const RequestsAutoRefundTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "created_timestamp", label: "Request date", active: true },
  { field_id: "updated_timestamp", label: "Update status date", active: true },
  { field_id: "current_amount", label: "Current amount", active: true },
  { field_id: "requested_amount", label: "Requested amount", active: true },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "business_url", label: "Business URL", active: true },

  { field_id: "admin_comment", label: "Comment", active: true },
  { field_id: "admin_screenshots", label: "Screenshots", active: true }
]

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "request_status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    request_status: "all",
    created_timestamp: null,
    updated_timestamp: null,
    request_type: "all",
    enrollments: [],
    merchant_descriptor: "all",
    arn_codes: [],
    user_full_name: [],
    user_email: [],
    business_account: [],
    business_url: [],
    enable_auto_refunds: "all",
    auto_refund_amount_other: { min: null, max: null, type: null },
    auto_refund_amount_rdr: { min: null, max: null, type: null },
    exclude_fields: [],
    is_archived: null,
    admin_comment: [],
    admin_screenshots: "all"
  },

  fetchingFilterOptions: false,
  filterOptions: {
    request_status: [],
    request_type: [],
    admin_comment: []
  },

  visibleFiltersAdmin: {
    request_status: true,
    user_email: true,
    user_full_name: true,
    business_account: true
  },

  exportFetching: false,
  exportResult: null,

  saveResult: null
})

/* ------------- Selectors ------------- */
export const selectAreFiltersDefault = (state, folder) => {
  const defaultFilters = defaultFiltersByFolders.auto_refund[folder] || INITIAL_STATE.filters

  const filtersToCompare = {
    ...INITIAL_STATE.filters,
    request_status: defaultFilters.request_status,
    is_archived: defaultFilters.is_archived
  }

  return isEqual(filtersToCompare, state.requestsAutoRefund.filters)
}

/* ------------- Functions for reducer cases ------------- */
const getRequestsAutoRefundFilterOptionsRequest = (state) =>
  state.merge({ fetchingFilterOptions: true })
const getRequestsAutoRefundFilterOptionsSuccess = (state, { payload: filterOptions }) =>
  state.merge({ fetchingFilterOptions: false, filterOptions })
const getRequestsAutoRefundFilterOptionsFailure = (state) =>
  state.merge({ fetchingFilterOptions: false })

const getRequestsAutoRefundRequest = (state) => state.merge({ fetching: true })
const getRequestsAutoRefundSuccess = (state, { payload: result }) => {
  if (result.results) {
    return state.merge({ fetching: false, result })
  } else {
    return state.merge({
      fetching: false,
      result: {
        count: result?.length || 0,
        results: result
      }
    })
  }
}

const updateRequestsAutoRefundRequest = (state) => state.merge({ fetching: true })
const updateRequestsAutoRefundSuccess = (state, { payload: autoRefund }) => {
  let isChangedArchived = false
  let newResults = state.result.results?.map((el) => {
    if (el.id === autoRefund.id) {
      if (el.is_archived !== autoRefund.is_archived) isChangedArchived = true
      return { ...el, request_status: autoRefund.request_status }
    }
    return el
  })
  return state.merge({
    fetching: false,
    result: {
      ...state.result,
      results: newResults
    },
    saveResult: isChangedArchived ? (autoRefund.is_archived ? "archived" : "unarchived") : "updated"
  })
}

const deleteRequestsAutoRefundRequest = (state) => state.merge({ fetching: true })
const deleteRequestsAutoRefundSuccess = (state, { payload: autoRefund }) => {
  let newResults = state.result.results?.filter((el) => el.id !== autoRefund.id)
  return state.merge({
    fetching: false,
    result: {
      count: state.result.count - 1,
      results: newResults
    }
  })
}

const requestsAutoRefundFailure = (state) => state.merge({ fetching: false })

export const requestsAutoRefundSetProp = (state, { key, value }) => {
  const obj = {}
  if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
    if (key === "filters") obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state?.[key], ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

export const clearRequestsAutoRefundFilters = (state, { payload: data }) => {
  return state.merge({ filters: { ...INITIAL_STATE.filters, ...data } })
}

const getRequestsAutoRefundExportRequest = (state) => state.merge({ exportFetching: true })
const getRequestsAutoRefundExportSuccess = (state, { payload: exportResult }) =>
  state.merge({
    exportFetching: false,
    exportResult
  })
const getRequestsAutoRefundExportFailure = (state) => state.merge({ exportFetching: false })

const logout = (state) => {
  return state.merge({
    ...INITIAL_STATE,
    filters: state.filters,
    columnsAdmin: state.columnsAdmin,
    visibleFiltersAdmin: state.visibleFiltersAdmin
  })
}

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const REQUESTS_AUTO_REFUND_PREFERENCES_VERSION = 1

const applyPreferences = (state, { payload }) => {
  const preference = payload?.["requests-auto-refund"]?.data
  if (!preference || preference.version !== REQUESTS_AUTO_REFUND_PREFERENCES_VERSION) return state
  if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
    return state

  return state.merge({
    filters: preference.filters,
    columnsAdmin: preference.columnsAdmin,
    visibleFiltersAdmin: preference.visibleFiltersAdmin
  })
}

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_REQUESTS_AUTO_REFUND_FILTER_OPTIONS_REQUEST]:
    getRequestsAutoRefundFilterOptionsRequest,
  [Types.GET_REQUESTS_AUTO_REFUND_FILTER_OPTIONS_SUCCESS]:
    getRequestsAutoRefundFilterOptionsSuccess,
  [Types.GET_REQUESTS_AUTO_REFUND_FILTER_OPTIONS_FAILURE]:
    getRequestsAutoRefundFilterOptionsFailure,

  [Types.GET_REQUESTS_AUTO_REFUND_REQUEST]: getRequestsAutoRefundRequest,
  [Types.GET_REQUESTS_AUTO_REFUND_SUCCESS]: getRequestsAutoRefundSuccess,

  [Types.UPDATE_REQUESTS_AUTO_REFUND_REQUEST]: updateRequestsAutoRefundRequest,
  [Types.UPDATE_REQUESTS_AUTO_REFUND_SUCCESS]: updateRequestsAutoRefundSuccess,

  [Types.DELETE_REQUESTS_AUTO_REFUND_REQUEST]: deleteRequestsAutoRefundRequest,
  [Types.DELETE_REQUESTS_AUTO_REFUND_SUCCESS]: deleteRequestsAutoRefundSuccess,

  [Types.REQUESTS_AUTO_REFUND_FAILURE]: requestsAutoRefundFailure,

  [Types.CLEAR_REQUESTS_AUTO_REFUND_FILTERS]: clearRequestsAutoRefundFilters,

  [Types.REQUESTS_AUTO_REFUND_SET_PROP]: requestsAutoRefundSetProp,

  [Types.GET_REQUESTS_AUTO_REFUND_EXPORT_REQUEST]: getRequestsAutoRefundExportRequest,
  [Types.GET_REQUESTS_AUTO_REFUND_EXPORT_SUCCESS]: getRequestsAutoRefundExportSuccess,
  [Types.GET_REQUESTS_AUTO_REFUND_EXPORT_FAILURE]: getRequestsAutoRefundExportFailure,

  [Types.LOGOUT]: logout,

  [preferencesSuccess.type]: applyPreferences
})
